import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    Box,
    Chip,
    IconButton,
    Fade,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';

const AccountSelectionModal = ({ open, accounts, onSelect, onClose }) => {
    // Branding constants matching student portal color scheme
    const BRAND_ORANGE = '#ff502d';
    const BRAND_GRADIENT = 'linear-gradient(135deg, #ff502d 0%, #ff785a 100%)';

    const getRoleColor = (role) => {
        const roleColors = {
            teacher: BRAND_ORANGE,
            student: '#4CAF50',
            admin: '#e74c3c',
            supporter: '#f1c40f',
        };
        return roleColors[role?.toLowerCase()] || '#94a3b8';
    };

    const formatMaskedInfo = (email, phone) => {
        const parts = [];
        if (email) parts.push(email);
        if (phone) parts.push(phone);
        return parts.join(' • ');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Fade}
            transitionDuration={400}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '24px !important',
                    maxHeight: 'min(620px, 85vh)',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
                    background: '#ffffff !important',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        background: BRAND_GRADIENT,
                        zIndex: 1
                    }
                }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.4)',
                        backdropFilter: 'blur(8px)',
                    },
                },
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                pt: 4,
                px: 4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
            }}>
                <Box sx={{ pr: 3 }}>
                    <Typography
                        variant="h5"
                        fontWeight={800}
                        sx={{
                            color: '#1e293b',
                            mb: 0.5,
                            fontFamily: "'Poppins', sans-serif",
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Welcome Back!
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#64748b',
                            fontFamily: "'Poppins', sans-serif",
                            lineHeight: 1.6
                        }}
                    >
                        We found multiple accounts associated with your credentials. Please select the one you'd like to use.
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: '#94a3b8',
                        background: '#f8fafc',
                        '&:hover': { background: '#f1f5f9', color: BRAND_ORANGE }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    pt: 2,
                    pb: 4,
                    px: 4,
                    overflowY: 'auto',
                    flex: 1,
                    /* Custom scrollbar */
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f5f9',
                        borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#cbd5e1',
                        borderRadius: '10px',
                        '&:hover': {
                            background: '#94a3b8',
                        },
                    },
                }}
            >
                <List sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    pt: 1,
                    pb: 1
                }}>
                    {accounts?.map((account, index) => (
                        <ListItem
                            key={account.id || index}
                            disablePadding
                        >
                            <ListItemButton
                                onClick={() => onSelect(account)}
                                sx={{
                                    borderRadius: '16px',
                                    border: '1px solid',
                                    borderColor: '#f1f5f9',
                                    p: 2.5,
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    backgroundColor: '#f8fafc',
                                    '&:hover': {
                                        borderColor: BRAND_ORANGE,
                                        backgroundColor: '#ffffff',
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 16px 20px -4px ${BRAND_ORANGE}15, 0 6px 8px -4px ${BRAND_ORANGE}10`,
                                    },
                                }}
                            >
                                <ListItemAvatar sx={{ mr: 2 }}>
                                    <Avatar
                                        src={account.profile_image}
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            bgcolor: BRAND_ORANGE + '10',
                                            color: BRAND_ORANGE,
                                            border: `3px solid #fff`,
                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                        }}
                                    >
                                        {!account.profile_image && <PersonIcon sx={{ fontSize: 36 }} />}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={700}
                                                sx={{
                                                    color: '#1e293b',
                                                    fontFamily: "'Poppins', sans-serif",
                                                    fontSize: '1.1rem'
                                                }}
                                            >
                                                {account.full_name || account.name || 'User'}
                                            </Typography>
                                            <Chip
                                                label={account.role || 'User'}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    bgcolor: getRoleColor(account.role) + '15',
                                                    color: getRoleColor(account.role),
                                                    border: `1px solid ${getRoleColor(account.role)}30`,
                                                    fontFamily: "'Poppins', sans-serif"
                                                }}
                                            />
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#64748b',
                                                    mb: 1,
                                                    fontFamily: "'Poppins', sans-serif",
                                                    fontWeight: 500
                                                }}
                                            >
                                                {formatMaskedInfo(account.email, account.phone_number)}
                                            </Typography>
                                            {(account.student_id || account.teacher_id) && (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: BRAND_ORANGE,
                                                            fontWeight: 700,
                                                            bgcolor: BRAND_ORANGE + '10',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: '8px',
                                                            fontFamily: "'Poppins', sans-serif",
                                                            fontSize: '0.7rem',
                                                            border: `1px solid ${BRAND_ORANGE}20`
                                                        }}
                                                    >
                                                        {account.student_id ? (account.student_id.startsWith('SID-') ? account.student_id : `SID-${account.student_id}`) : `TID-${account.teacher_id}`}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
        </Dialog>
    );
};

export default AccountSelectionModal;
